import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Home, Dices, Settings, LogOut, Lightbulb, PieChart, Calendar, Shield, ArrowLeft, Building2, HelpCircle, CircleUser, FileText } from 'lucide-react';
import { fetchClubBetaFeatures, isBetaEnabled } from '../utils/betaFeatures';
import { useI18n } from '../i18n';

const Navigation = ({ user, onLogout }) => {
  const { t } = useI18n(user?.club?.language);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showClubInfo, setShowClubInfo] = useState(false);
  const [gameNightsEnabled, setGameNightsEnabled] = useState(false);
  const infoRef = useRef(null);
  const legalMenuRef = useRef(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);
  const closeLegalMenu = () => {
    closeMenu();
    if (legalMenuRef.current) legalMenuRef.current.open = false;
  };
  
  // Check if SuperAdmin is impersonating a club
  const isImpersonating = user?.isImpersonating;
  const clubName = user?.club?.name || t.nav.club;
  const clubDescription = user?.club?.description?.trim() || '';
  const clubLogoUrl = (user?.club?.logo_url || '').trim();

  useEffect(() => {
    let cancelled = false;
    fetchClubBetaFeatures().then((features) => {
      if (!cancelled) setGameNightsEnabled(isBetaEnabled(features, 'game_nights'));
    });
    return () => { cancelled = true; };
  }, [user?.club?.id]);

  useEffect(() => {
    if (!showClubInfo) return;
    const onPointerDown = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setShowClubInfo(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [showClubInfo]);

  useEffect(() => {
    closeMenu();
    if (legalMenuRef.current) legalMenuRef.current.open = false;
  }, [location.pathname]);

  // Handle returning to SuperAdmin from impersonation
  const handleBackToSuperAdmin = () => {
    // Restore original SuperAdmin session
    const originalData = localStorage.getItem('masaklubi_superadmin_original');
    
    if (originalData) {
      try {
        const { user, token, clubKey } = JSON.parse(originalData);
        
        // Restore original session
        localStorage.setItem('masaklubi_user', JSON.stringify(user));
        if (token) localStorage.setItem('masaklubi_access_token', token);
        if (clubKey) localStorage.setItem('masaklubi_club_key', clubKey);
        
        // Clean up impersonation data
        localStorage.removeItem('masaklubi_superadmin_original');
        
        console.log('[Navigation] Restored SuperAdmin session');
        
        // Redirect to SuperAdmin dashboard
        window.location.href = '/superadmin';
        return;
      } catch (err) {
        console.error('[Navigation] Failed to restore SuperAdmin session:', err);
      }
    }
    
    // Fallback: clear everything and go to root
    localStorage.removeItem('masaklubi_user');
    localStorage.removeItem('masaklubi_access_token');
    localStorage.removeItem('masaklubi_club_key');
    localStorage.removeItem('masaklubi_superadmin_verified');
    localStorage.removeItem('masaklubi_superadmin_original');
    window.location.href = '/';
  };

  return (
    <nav className="navbar">
      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="impersonation-banner">
          <Building2 size={16} />
          <span>{t.nav.viewingClub} <strong>{clubName}</strong></span>
          <button onClick={handleBackToSuperAdmin} className="back-to-superadmin">
            <ArrowLeft size={14} /> {t.nav.backToSuperAdmin}
          </button>
        </div>
      )}
      
      <div className="nav-brand">
        {clubLogoUrl ? (
          <img src={clubLogoUrl} alt="" className="nav-brand-logo" />
        ) : (
          <Dices />
        )}
        {clubName}
      </div>
      
      <button className="mobile-menu-btn" onClick={toggleMenu}>
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <div className={`nav-links ${isOpen ? 'open' : ''}`}>
        <NavLink to="/" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <Home size={18} /> {t.nav.home}
        </NavLink>
        <NavLink to="/games" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <Dices size={18} /> {t.nav.games}
        </NavLink>
        {gameNightsEnabled && (
          <NavLink to="/events" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
            <Calendar size={18} /> {t.nav.events}
          </NavLink>
        )}
        <NavLink to="/statistics" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <PieChart size={18} /> {t.nav.analytics}
        </NavLink>
        <NavLink to="/suggestions" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <Lightbulb size={18} /> {t.nav.suggestions}
        </NavLink>
        {(user.role === 'admin' || user.role === 'board' || isImpersonating) && (
          <NavLink to="/admin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
            <Settings size={18} /> {t.nav.admin}
          </NavLink>
        )}
        {user.role === 'superadmin' && !isImpersonating && (
          <>
            <NavLink to="/superadmin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
              <Shield size={18} /> {t.nav.superAdmin}
            </NavLink>
            <NavLink to="/admin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
              <Settings size={18} /> {t.nav.admin}
            </NavLink>
          </>
        )}
        <details ref={legalMenuRef} style={{ position: 'relative' }}>
          <summary className="nav-link" style={{ cursor: 'pointer', listStyle: 'none' }}>
            <FileText size={18} /> {t.legal.menu}
          </summary>
          <div style={{
            position: 'absolute',
            right: 0,
            zIndex: 1000,
            minWidth: '210px',
            padding: '0.45rem',
            borderRadius: '8px',
            background: '#1f2937',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
          }}>
            <NavLink to="/legal/license" className="nav-link" onClick={closeLegalMenu}>{t.legal.licenseLink}</NavLink>
            <NavLink to="/legal/privacy" className="nav-link" onClick={closeLegalMenu}>{t.legal.privacyLink}</NavLink>
            <NavLink to="/legal/terms" className="nav-link" onClick={closeLegalMenu}>{t.legal.termsLink}</NavLink>
          </div>
        </details>
        {clubDescription && (
          <div className="nav-club-info" ref={infoRef}>
            <button
              type="button"
              className="nav-link nav-club-info-btn"
              aria-label={t.nav.clubDescription}
              aria-expanded={showClubInfo}
              title={t.nav.clubDescription}
              onClick={() => setShowClubInfo((v) => !v)}
            >
              <HelpCircle size={18} />
            </button>
            {showClubInfo && (
              <div className="nav-club-info-popover" role="dialog" aria-label={t.nav.clubDescription}>
                <p>{clubDescription}</p>
              </div>
            )}
          </div>
        )}
        {user?.name && (
          <span className="nav-link nav-user" title={user.name}>
            <CircleUser size={18} />
            <span className="nav-user-name">{user.name}</span>
          </span>
        )}
        <button
          onClick={onLogout}
          className="nav-link"
          aria-label={t.nav.logout}
          title={t.nav.logout}
          style={{background: 'none', border: 'none', cursor: 'pointer'}}
        >
          <LogOut size={18} />
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
